<template>
    <App title="Station Discount Management">
	<div class="station-discount-page">
		<!-- Header Section -->
		<div class="page-header mb-4">
			<div class="d-flex justify-content-between align-items-center">
				<div>
					<h4 class="fw-semibold mb-1">Station Discount Management</h4>
					<p class="text-muted small mb-0">Configure battery swap discounts for stations</p>
				</div>
				<button 
					class="btn btn-dark btn-sm px-3" 
					@click="showAddForm = !showAddForm"
					v-if="canAdd && !showAddForm"
				>
					<i class="fas fa-plus me-2"></i>New Discount
				</button>
			</div>
		</div>

		<!-- Add Discount Form -->
		<transition name="fade">
			<div v-if="showAddForm" class="mb-4">
				<div class="card border-0 shadow-sm">
					<div class="card-header bg-white border-0 py-3">
						<h6 class="mb-0 fw-semibold">Add New Discount</h6>
					</div>
					<div class="card-body">
						<DiscountForm
							:url="route('discount.ajax.create')"
							:pricing-url="route('price.ajax.lookup')"
							:battery-types="orderTypes"
							@success="handleDiscountSuccess"
							@error="handleDiscountError"
							@cancel="showAddForm = false"
						/>
					</div>
				</div>
			</div>
		</transition>

		<!-- Discount List -->
		<div class="discount-list">
			<!--
			<div v-if="discounts.length === 0" class="text-center py-5">
				<i class="fas fa-inbox fa-3x text-muted mb-3"></i>
				<p class="text-muted">No discounts configured yet</p>
			</div>
			-->
			<div class="row g-3">

                <!-- Discount Datatable -->
				 <!-- Pricing Datatable -->
				 <Datatable :options="options" ref="discountDatatable"/>
				
			</div>
		</div>
	</div>
    </App>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'
import { Role, hasRole } from '@/models/Admin'
import DiscountForm from '@/vue/components/DiscountForm.vue'
import App from '@/vue/templates/App.vue'
import Datatable from '@/vue/components/Datatable.vue'
import { CellRender, Column } from '@/vue/utils/Datatable'
import Swal from 'sweetalert2'
interface Station {
	value: string
	name: string
}

interface OrderType {
	value: string
	name: string
}

interface Discount {
	id: string
	station: string
	stationName: string
	batteryType: string
	batteryTypeName: string
	basePrice: number
	discountedPrice: number
	numberOfSwaps: number
	createdAt: string
	effectiveDate: string
	status: 'Active' | 'Scheduled'
}

@Component({
	components: {
		DiscountForm,
        App,
        Datatable
	}
})
class Discount extends Vue {
	

	@Prop({ type: Array, default: () => [] })
	readonly orderTypes!: OrderType[]
	showAddForm = false
	discounts: Discount[] = []

	get canAdd() {
		return hasRole(this.$user, [Role.SUPER_ADMIN, Role.COUNTRY_MANAGER])
	}

	handleDiscountSuccess(event: any) {
		this.showToast(event.message, 'success')
		this.showAddForm = false
		// Refresh discount list here if needed
		this.handleDiscountTableRefresh()
	}

	handleDiscountError(event: any) {
		this.showToast(event.message, 'error')
	}

	handleDiscountTableRefresh() {
		// @ts-ignore
		const datatableRef = this.$refs.discountDatatable as any
		if (datatableRef && datatableRef.datatable) {
			datatableRef.datatable.ajax.reload(null, false)
		} 
	}

	getStatusBadgeClass(status: string): string {
		return status === 'Active' ? 'bg-success' : 'bg-warning'
	}

	formatCurrency(amount: number): string {
		return new Intl.NumberFormat('en-NG', {
			style: 'currency',
			currency: 'NGN',
			minimumFractionDigits: 0
		}).format(amount / 100)
	}

	formatDate(date: string): string {
		return new Date(date).toLocaleDateString('en-NG', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		})
	}

	calculateSavings(basePrice: number, discountedPrice: number): string {
		const savings = basePrice - discountedPrice
		const percentage = ((savings / basePrice) * 100).toFixed(1)
		return `${this.formatCurrency(savings)} (${percentage}%)`
	}

	showToast(message: string, type: 'success' | 'error' ) {
        
        Swal.fire({
            toast: true,
            position: 'top-end', 
            icon: type, 
            title: message,
            showConfirmButton: false, 
            timer: 5000, 
            timerProgressBar: true,
            didOpen: (toast) => {
                // Pause the timer when the mouse hovers over the toast
                toast.addEventListener('mouseenter', Swal.stopTimer)
                // Resume the timer when the mouse leaves
                toast.addEventListener('mouseleave', Swal.resumeTimer)
            }
        })
    }

	get buttons() {
		let buttons: any[] = []
		buttons.push('refresh')
		
		buttons.push('trashed')

		return buttons
	}

	get columns() {
		let columns: any[] = []
		columns.push({
			data: 'id',
			title: 'ID',
		})
		columns.push({
			data: 'price.station.title',
			title: 'Station',
		})
		columns.push({
			data: 'price.price_label',
			title: 'Order Type',
		})
		columns.push({
			data: 'threshold',
			title: 'Threshold',
		})
		columns.push({
			data: 'price.amount',
			title: 'Base Price',
			render: CellRender.money(),
		})
		columns.push({
			data: 'amount',
			title: 'Discounted Amount',
			render: CellRender.money(),
		})
		
		
		columns.push({
			data: 'activated_at',
			title: 'Effective Date',
			render: CellRender.date(),
			orderable: true,
		
		})

		columns.push({
			data: 'created_at',
			title: 'Registered',
			render: CellRender.date(),
			orderable: true,
			name: 'created_at',
		})

		columns.push(Column.actions(this.$gate, { view: false , delete: true , update: true }))

		return columns
	}

	get options() {
		return {
			ajax: {
				url: this.route('discount.ajax.table'),
				method: 'POST',
			},
			buttons: this.buttons,
			columns: this.columns,
		}
	}
}

export default toNative(Discount)
</script>

<style scoped>
.station-discount-page {
	padding: 1.5rem;
	max-width: 1400px;
	margin: 0 auto;
}

.page-header h4 {
	color: #212529;
	font-size: 1.5rem;
}

.discount-card {
	transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.discount-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
}

.badge {
	font-weight: 500;
	padding: 0.35rem 0.65rem;
}

.bg-light {
	background-color: #f8f9fa !important;
}

.bg-success {
	background-color: #198754 !important;
	color: white !important;
}

.bg-warning {
	background-color: #ffc107 !important;
	color: #212529 !important;
}

.text-success {
	color: #0f5132 !important;
}

.text-muted {
	color: #6c757d !important;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
	opacity: 0;
}

.slide-down-enter-active, .slide-down-leave-active {
	transition: all 0.3s ease;
	max-height: 100px;
	overflow: hidden;
}

.slide-down-enter-from, .slide-down-leave-to {
	max-height: 0;
	opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
	.station-discount-page {
		padding: 1rem;
	}

	.page-header h4 {
		font-size: 1.25rem;
	}
}
</style>